import {
  HelpOutline as HelpOutlineIcon,
  MoneyOff as MoneyOffIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import React from "react";
import type {
  ContractType,
  DocType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useContextMenu } from "../../commons/hooks/useContextMenu.js";
import { ConfirmModalProps } from "../../commons/modals/ConfirmProcess.js";
import { ExpandedStore } from "../../trees-virtual/hooks/useExpandedStore.js";
import { AdditionalItemInput } from "../../trees-virtual/types.js";
import { RIGHT_ICON_STYLE } from "../../trees/BareTreeTableItem.js";
import {
  TreeDataIconsWrapper,
  TreeDataPath,
} from "../../trees/components/TreeDataContainer.js";
import { TreeTitle } from "../../trees/components/TreeTitle.js";
import { DocItemContextMenuItems } from "../doc-items/menus/DocItemContextMenuItems.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type {
  DocDataGridItemComponent,
  DocDataGridItemComponentProps,
} from "./DocDataGrid.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreateDocDataGridItemCellOptions<T extends DocDataGrid_ItemFragment> {
  pathToDocPage: string;
  projectId: string;
  doc: {
    id: string;
    rootItemId: string;
    viewerPermissions: PermissionName[];
    owningSystemOrganisationId: string;
    contractType: ContractType;
    type: DocType;
  };
  startConfirmProcess: (
    props: ConfirmModalProps
  ) => Promise<boolean | undefined>;
  navigateToItem: (id: string) => void;
  expandedStore: ExpandedStore;
  pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
  isAllowedToPasteItem: (item: DocDataGrid_ItemFragment) => boolean;
  onAdditionalItemInputChange: ((input: AdditionalItemInput) => void) | null;
  additionalMenuItems?: (item: T) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createDocDataGridItemCell<T extends DocDataGrid_ItemFragment>({
  pathToDocPage,
  projectId,
  doc,
  startConfirmProcess,
  navigateToItem,
  expandedStore,
  pasteItem,
  isAllowedToPasteItem,
  onAdditionalItemInputChange,
  additionalMenuItems,
}: CreateDocDataGridItemCellOptions<T>): DocDataGridItemComponent<T> {
  const DocDataGridItemCell = ({
    item,
    row,
    parentItem,
  }: DocDataGridItemComponentProps<T>) => {
    const theme = useTheme();
    const showHelpIcon =
      item.type === "decision" || item.decisionIsContingentItem;

    const [handleContextMenu, menuElement] = useContextMenu<HTMLDivElement>(
      <>
        {additionalMenuItems?.(item)}
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={doc.id}
          docType={doc.type}
          projectId={projectId}
          item={item}
          itemParentType={parentItem?.type ?? null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedStore.expandedItemIds}
          pasteItem={isAllowedToPasteItem(item) ? pasteItem : null}
          onAdditionalItemInputChange={onAdditionalItemInputChange}
        />
      </>
    );

    return (
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        gap={0.5}
        sx={{ cursor: "pointer", overflow: "hidden" }}
        onClick={() => {
          navigateToItem(item.id);
          expandedStore.setItemExpanded(item.id, true);
        }}
        onContextMenuCapture={handleContextMenu}
      >
        {showHelpIcon && (
          <HelpOutlineIcon
            fontSize="small"
            style={{ color: theme.palette.warning.main, flexShrink: 0 }}
          />
        )}

        <TreeTitle
          $bold={item.type === "section"}
          $depth={row.depth}
          style={{ flex: 1, minWidth: 0 }}
        >
          {item.pathForPdf && (
            <TreeDataPath $depth={row.depth}>{item.pathForPdf}</TreeDataPath>
          )}
          {item.title}
        </TreeTitle>

        <TreeDataIconsWrapper>
          {item.isHidden && (
            <span>
              <VisibilityOffIcon style={RIGHT_ICON_STYLE} />
            </span>
          )}
          {item.isPriceHidden && !item.isHidden && (
            <span>
              <MoneyOffIcon style={RIGHT_ICON_STYLE} />
            </span>
          )}
        </TreeDataIconsWrapper>

        {menuElement}
      </Box>
    );
  };

  DocDataGridItemCell.displayName = "DocDataGridItemCell";

  return DocDataGridItemCell;
}
